import { useState, type FormEvent } from "react";
import { errorText } from "../../i18n/serverErrors";
import { api } from "../../api/apiClient";
import { shopModeApi } from "../../api/shopMode";
import type { Task } from "../../api/types";
import { useT } from "../../i18n/LanguageProvider";
import { Modal } from "../Modal";

interface CompleteTaskModalProps {
  task: Task;
  mode?: "crm" | "floor";
  onClose: () => void;
  onCompleted: () => Promise<void>;
}

export function CompleteTaskModal({
  task,
  mode = "crm",
  onClose,
  onCompleted,
}: CompleteTaskModalProps) {
  const { t } = useT();
  const [photo, setPhoto] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (task.requireCompletionPhoto && !photo) return;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      if (photo) form.append("image", photo);
      if (mode === "floor") {
        await shopModeApi.floorCompleteTask(task.id, form);
      } else {
        await api.completeTask(task.id, form);
      }
      await onCompleted();
      onClose();
    } catch (err: unknown) {
      setError(errorText(err, "tasks.couldNotComplete"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={t("tasks.completeTitle", { name: task.name })} onClose={onClose}>
      <form
        style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}
        onSubmit={(event) => void submit(event)}
      >
        {error ? <div className="alert error">{error}</div> : null}
        {task.requireCompletionPhoto ? (
          <div className="alert">{t("tasks.completionPhotoRequired")}</div>
        ) : null}
        <div className="field">
          <label htmlFor="task-completion-photo">{t("tasks.completionPhoto")}</label>
          <input
            id="task-completion-photo"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
            disabled={busy}
          />
        </div>
        <div className="button-row">
          <button type="button" className="button plain" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="button"
            disabled={busy || (task.requireCompletionPhoto && !photo)}
          >
            {busy ? t("tasks.completing") : t("tasks.completeTask")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
